import { CourseEmployeeFilterI, CourseEmployeeI, CourseEmployeeEditI } from 'types';
import useFetchData from './useFetchData';
import useSwitch from './useSwitch';
import useTranslation from './useTranslation';
import employeesApi from 'Api/axios/employees-api';
import { useEffect, useCallback, useState } from 'react';
import lessonApi from 'Api/axios/lesson-api';
import useModal from './useModal';
import coursesApi from 'Api/axios/courses-api';
import { toast } from 'sonner';
import { colors } from 'Constants/Colors';

const useCoursesLessonsPage = (courseId: string, lessonId: string) => {
  const { t2 } = useTranslation('courses_lessons');

  // Custom Hooks
  const {
    allData: employees,
    fetchAll: fetchEmployees,
    loading: loadingEmployees,
  } = useFetchData<CourseEmployeeI, CourseEmployeeFilterI>({
    getAll: useCallback(
      (filter?: CourseEmployeeFilterI) => employeesApi.readEmployeesByCourseId(courseId, lessonId, filter),
      [courseId, lessonId]
    ),
  });

  // Obtenemos información de la evaluación actual
  const { data: lesson, fetchOne: fetchLesson } = useFetchData({
    getOne: useCallback(() => lessonApi.getLesson(lessonId), [lessonId]),
  });

  // Switch para publicar todos los empleados
  const { active: publishAll, toggle: togglePublishAll } = useSwitch({
    initialState: false,
  });

  // Switch para editar la evaluación
  const {
    active: disabled,
    toggle: toggleDisabled,
    activate: activateDisabled,
    deactivate: deactivateDisabled,
  } = useSwitch({
    initialState: true,
  });

  // Modal para guardar los cambios
  const { open: saveModal, openModal: openSaveModal, closeModal: closeSaveModal } = useModal();

  // Estado para guardar los valores de los empleados que se van a editar.
  const [editValues, setEditValues] = useState<CourseEmployeeEditI>({});

  // Estado para mostrar el backdrop de carga.
  const [loading, setLoading] = useState(false);

  // Al obtener a los empleados, se guardan los valores de los empleados que se pueden editar.
  useEffect(() => {
    // Obtenemos los valores ya editados.
    setEditValues(prev => {
      const anteriores = { ...prev };

      // Para cada empleado, se guardan si no existen.
      employees.forEach(employee => {
        if (!anteriores[employee.id]) {
          anteriores[employee.id] = {
            userId: employee.id,
            grade: employee.grade.toString(),
            published: !!employee.published,
            userEvaluationId: employee.userEvaluationId,
            edited: false,
          };
        }
      });

      return anteriores;
    });
  }, [employees]);

  // Cambiamos el valor de un campo de un empleado
  const onFieldChange = useCallback((employeeId: string, field: string, value: any) => {
    setEditValues(prev => {
      return {
        ...prev,
        [employeeId]: {
          ...prev[employeeId],
          [field]: value,
          edited: true,
        },
      };
    });
  }, []);

  // Obttenemos el valor de un empleado
  const getEmployeeValue = useCallback(
    (employeeId: string, field: string) => {
      return editValues[employeeId]?.[field];
    },
    [editValues]
  );

  // Al enviar el formulario
  const onSubmit = useCallback(async () => {
    setLoading(true);
    closeSaveModal();

    // Obtenemos los valores que han sido editados.
    const editedValues = Object.values(editValues).filter(value => value.edited);

    if (editedValues.length === 0) {
      toast.error(t2('form.error.no_changes'), {
        style: {
          backgroundColor: colors.custom.toast.info,
          color: colors.BLACK,
          fontWeight: 600,
          border: 'none',
        },
        richColors: true,
      });
      onReset();
      setLoading(false);
      return;
    }

    // Filtramos los valores que han sido editados para enviar al backend.
    const employeesToUpdate = editedValues.reduce((acc, value) => {
      acc[value.userId] = {
        ...value,
      };
      return acc;
    }, {});

    const response = await coursesApi.updateGrades(courseId, lessonId, employeesToUpdate);

    if (response.data) {
      onReset();
    }

    setLoading(false);
  }, [editValues]);

  // Método para manejar los filtros de busqueda.
  const onSearch = useCallback(
    (filters: CourseEmployeeFilterI) => {
      fetchEmployees(filters);
    },
    [fetchEmployees]
  );

  // Método para manejar el cambio de estado de publicación para todos los empleados.
  const onPublishChange = useCallback((estatus: boolean) => {
    setEditValues(prev => {
      // Para cada empleado, se actualiza el estado de publicación.
      const nuevos = Object.fromEntries(
        Object.entries(prev).map(([key, value]) => [key, { ...value, published: estatus, edited: true }])
      );

      return nuevos;
    });
    togglePublishAll(estatus);
  }, []);

  // Cargar empleados al cambiar course o lesson
  useEffect(() => {
    fetchEmployees();
    fetchLesson(lessonId);
  }, [courseId, lessonId, fetchEmployees, fetchLesson]);

  // Reseteamos los valores de los empleados
  const onReset = useCallback(() => {
    setEditValues({});
    togglePublishAll(false);
    activateDisabled();
    fetchEmployees();
  }, [fetchEmployees, togglePublishAll]);

  return {
    t2,
    lesson,
    employees,
    disabled,
    onFieldChange,
    toggleDisabled,
    loadingEmployees,
    activateDisabled,
    deactivateDisabled,
    editValues,
    getEmployeeValue,
    onSearch,
    onPublishChange,
    publishAll,
    saveModal,
    openSaveModal,
    closeSaveModal,
    onReset,
    onSubmit,
    loading,
  };
};

export default useCoursesLessonsPage;
